import React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router';
import { Breadcrumb, BreadcrumbProps } from '../Breadcrumb';
import { IconButton } from '../Buttons/IconButton';
import { Typography } from '../Typography';
import { BreadcrumbsProps, getBreadcrumbItems, getMenuBreadcrumbs } from './utils';
/**
 * Renders separated Breadcrumbs, acts as a a wrapper for the Breadcrumb and provides a simple way to set color and
 * variant at the group level.
 */
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  items,
  onCopy,
  copyAriaLabel,
  onCopyRef,
  copyRefLabel,
  highlightLastItem,
  t,
}: BreadcrumbsProps) => {
  const navigate = useNavigate();
  const isSmallDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement> | null) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement>, breadcrumb: BreadcrumbProps) => {
    event.stopPropagation();
    event.preventDefault();
    if (breadcrumb.onClick) {
      breadcrumb.onClick();
    }
    if (breadcrumb.href) {
      navigate(breadcrumb.href);
    }
    handleClose();
  };
  const copyRefTooltip = copyRefLabel || 'Copy reference';
  const copyRefAriaLabel = copyAriaLabel || 'Copy';
  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        aria-labelledby="more-breadcrumbs-menu"
        disableScrollLock
      >
        {getMenuBreadcrumbs(items, isSmallDevice).map((breadcrumb: BreadcrumbProps) => (
          <MenuItem key={`menu_breadcrumb_${breadcrumb.label}`} onClick={(e) => handleMenuItemClick(e, breadcrumb)}>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
              <Typography>{breadcrumb.label}</Typography>
              {breadcrumb.copyRef && onCopyRef && (
                <Tooltip title={copyRefTooltip}>
                  <IconButton
                    size="small"
                    aria-label={copyAriaLabel || 'Copy'}
                    sx={{ opacity: 0.8 }}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      onCopyRef(breadcrumb.copyRef!);
                      handleClose();
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>
      <Box display="flex" alignItems="center" gap={1}>
        <MuiBreadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: '#A4A7AE' }} />}>
          {getBreadcrumbItems(items, isSmallDevice, handleMoreClick).map((breadcrumb, idx) => (
            <Breadcrumb
              key={`breadcrumb_${idx}`}
              {...breadcrumb}
              highlightLastItem={highlightLastItem}
              copy={
                breadcrumb.copyRef && onCopyRef
                  ? {
                      copyRef: breadcrumb.copyRef,
                      onRef: onCopyRef,
                      tooltip: copyRefTooltip,
                      ariaLabel: copyRefAriaLabel,
                    }
                  : undefined
              }
            />
          ))}
        </MuiBreadcrumbs>
        {onCopy && (
          <Tooltip title={t ? t('common:copy') : 'Copy'}>
            <IconButton onClick={onCopy} size="small" aria-label={copyAriaLabel || 'Copy'} sx={{ opacity: 0.8 }}>
              <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </>
  );
};
